'use client'

import Image from 'next/image'
import {FC} from 'react'

import {GetStartedWithVerificationBanner} from '@/components/common/banner/GetStartedWithVerificationBanner'
import {Section} from '@/components/common/content/Section'
import {Hero} from '@/components/common/hero/Hero'
import {HeroBenefitList} from '@/components/common/hero/HeroBenefitList'
import {TrademarkRegistrationHeroVideo} from '@/components/common/hero/video/TrademarkRegistrationHeroVideo'
import {HowItWorks} from '@/components/common/how-it-works/HowItWorks'
import {MostRecentArticles} from '@/components/common/recent-articles/MostRecentArticles'
import {LicensedAttorneys} from '@/components/common/team/LicensedAttorneys'
import {WhyTramaComparison} from '@/components/common/why-trama/comparison/WhyTramaComparison'
import {BrandProtectionCardList} from '@/components/homepage/cards/BrandProtectionCardList'
import {CountriesOffer} from '@/components/homepage/countries/CountriesOffer'
import {OurClientsAndStatistics} from '@/components/homepage/OurClientsAndStatistics'
import {TrademarkImportance} from '@/components/homepage/TrademarkImportance'
import {Page} from '@/components/Page'
import {Testimonial} from '@/components/testimonial/Testimonial'
import {useHomepageContent} from '@/content/homepage'
import {useTranslation} from '@/i18n'
import {HERO_PADDING_DESKTOP, HERO_PADDING_MOBILE} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {Locale} from '@/utils/constants'
import {RecentArticleCard} from '@/utils/contentful'
import {useLanguage} from '@/utils/i18n/language'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

type HomePageProps = {
  numOfRegisteredTrademarks: number
  mostRecentArticles: RecentArticleCard[]
}

const HomePage: FC<React.PropsWithChildren<HomePageProps>> = ({
  numOfRegisteredTrademarks,
  mostRecentArticles,
}) => {
  const {t} = useTranslation()
  const language = useLanguage()

  const {header, hero, cards, countriesOffer, howItWorks, whyRegisterTm, getStartedBanner} =
    useHomepageContent()

  const isEnLanguage = language === Locale.en

  return (
    <>
      <Page
        mainCtaButton={{
          text: t(header.buttonLinkText),
          linkHref: header.buttonLinkHref,
          onClick: () => analytics.track('Click on Start registration (Homepage - Header)'),
        }}
      >
        <Hero
          title={t(hero.title)}
          subtitle={t(hero.subtitle)}
          primaryButtonLink={hero.primaryButtonLink}
          primaryButtonText={t(hero.primaryButtonText)}
          primaryButtonTrackedEvent="Click on primary button in Hero - Homepage"
          secondaryButtonLink={hero.pricingButtonLink}
          secondaryButtonText={t(hero.pricingButtonText)}
          secondaryButtonTrackedEvent="Click on secondary button in Hero - Homepage"
          benefits={<HeroBenefitList list={hero.list} />}
          alignItems={isEnLanguage ? 'center' : 'flex-end'}
        >
          {isEnLanguage ? (
            <TrademarkRegistrationHeroVideo
              trackedEventParameters={{section: 'Hero', page: 'Homepage'}}
            />
          ) : (
            <div className="image">
              <Image
                src={hero.imageSrc}
                alt={hero.imageAlt}
                sizes={`${MOBILE_SCREENSIZE} 150px, ${TABLET_SCREENSIZE} 250px, 395px`}
                fill
                priority
                fetchPriority="high"
                style={{objectFit: 'contain'}}
              />
            </div>
          )}
        </Hero>
        <OurClientsAndStatistics
          numOfRegisteredTrademarks={numOfRegisteredTrademarks}
          backgroundColor={theme.palette.background.default}
          backgroundOverlay={{color: theme.palette.background.butter, heightRatio: 0.5}}
          hasBoxShadow
        />
        <BrandProtectionCardList list={cards.list} />
        <Section hasPaddingTop>
          <LicensedAttorneys />
        </Section>
        <CountriesOffer
          description={countriesOffer.description}
          trackedEventParameters={{position: 'Countries offer - Homepage'}}
        />
        <Section hasPaddingTop>
          <WhyTramaComparison numOfRegisteredTrademarks={numOfRegisteredTrademarks} />
        </Section>
        <Section hasPaddingTop>
          <HowItWorks
            title={t(howItWorks.title)}
            steps={howItWorks.steps}
            buttonText={t(howItWorks.buttonText)}
            buttonLink={howItWorks.buttonLink}
          />
        </Section>
        <TrademarkImportance benefits={whyRegisterTm.benefits} />
        <Testimonial />
        {mostRecentArticles.length > 0 && (
          <Section hasPaddingTop hasPaddingBottom>
            <MostRecentArticles
              articles={mostRecentArticles}
              trackedEvent={{
                name: 'RecentBlogs-Homepage',
              }}
            />
          </Section>
        )}
        <GetStartedWithVerificationBanner
          title={t(getStartedBanner.title)}
          primaryButtonLink={getStartedBanner.primaryButtonLink}
          primaryButtonText={t(getStartedBanner.primaryButtonText)}
          primaryButtonTrackedEvent="GetStartedBanner-Homepage"
        />
      </Page>

      <style jsx>{`
        .image {
          position: absolute;
          width: 395px;
          height: 400px;
          right: ${theme.spacing[5]};
          margin-bottom: -${HERO_PADDING_DESKTOP};
        }

        @media ${TABLET_SCREENSIZE} {
          .image {
            position: relative;
            width: 250px;
            height: 250px;
            margin-top: ${theme.spacing[5]};
            right: 0;
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .image {
            width: 150px;
            height: 150px;
            margin-top: ${theme.spacing[4]};
            margin-bottom: -${HERO_PADDING_MOBILE};
          }
        }
      `}</style>
    </>
  )
}

export default HomePage
